// v4.0 — PWA install prompt. iOS Safari has no `beforeinstallprompt`, so we
// show clear Share → "Add to Home Screen" instructions with the app icon.
// Chromium browsers get a real Install button. Dismissal persists 30 days.
import { useEffect, useState } from 'react';

const DISMISS_KEY = 'sg_install_prompt_dismissed_at';
const DISMISS_DAYS = 30;

const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)')?.matches || window.navigator.standalone === true;

const isIos = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);

const isAndroid = () => /android/i.test(window.navigator.userAgent);

const recentlyDismissed = () => {
  const t = Number(localStorage.getItem(DISMISS_KEY) || 0);
  return t && Date.now() - t < DISMISS_DAYS * 864e5;
};

export const InstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    // Always capture the deferred Chromium event (the manual trigger needs
    // it even after a dismissal); auto-show honors the dismissal window.
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); if (!recentlyDismissed()) setShow(true); };
    window.addEventListener('beforeinstallprompt', onPrompt);
    let t;
    if (isIos() && !recentlyDismissed()) t = setTimeout(() => setShow(true), 4000);
    return () => { window.removeEventListener('beforeinstallprompt', onPrompt); if (t) clearTimeout(t); };
  }, []);

  // Exact-SHA P1 — manual fallback trigger (Profile → "Install the app").
  // iOS Safari never fires `beforeinstallprompt`, so users need an
  // on-demand way to see the install instructions, even after dismissal.
  useEffect(() => {
    const onManual = () => { if (!isStandalone()) setShow(true); };
    window.addEventListener('sg:show-install-prompt', onManual);
    return () => window.removeEventListener('sg:show-install-prompt', onManual);
  }, []);

  if (!show) return null;

  const dismiss = () => { localStorage.setItem(DISMISS_KEY, String(Date.now())); setShow(false); };
  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice.catch(() => {});
    dismiss();
  };

  return (
    <div data-testid="pwa-install-prompt" role="dialog" aria-label="Install StyleGenome"
      className="fixed left-4 right-4 md:left-auto md:right-10 md:w-[380px] z-50 rounded-2xl bg-[#0D0D0D] text-white shadow-2xl border border-[#C7A96B]/40 p-4"
      style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 10rem)' }}>
      <div className="flex items-start gap-3">
        <img src="/icons/icon-192.png" alt="StyleGenome app icon" className="w-11 h-11 rounded-xl border border-white/10" />
        <div className="min-w-0 flex-1">
          <p className="font-sans text-sm font-semibold">Install StyleGenome</p>
          {isIos() && !deferred ? (
            <p className="font-sans text-xs text-white/70 mt-1 leading-relaxed" data-testid="pwa-install-instructions-ios">
              Get the full-screen app: tap the{' '}
              <svg className="inline -mt-0.5" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#C7A96B" strokeWidth="2" aria-label="Share icon"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" /><polyline points="16 6 12 2 8 6" /><line x1="12" y1="2" x2="12" y2="15" /></svg>{' '}
              <span className="text-[#C7A96B]">Share</span> button, then choose{' '}
              <span className="text-[#C7A96B]">“Add to Home Screen”</span>.
            </p>
          ) : !deferred ? (
            /* Phase 5 closeout — no `beforeinstallprompt` on this browser:
               give actionable platform steps instead of just "Not now". */
            <p className="font-sans text-xs text-white/70 mt-1 leading-relaxed" data-testid="pwa-install-instructions-menu">
              {isAndroid() ? (
                <>Open your browser menu (⋮) and choose{' '}
                  <span className="text-[#C7A96B]">“Install app”</span> or{' '}
                  <span className="text-[#C7A96B]">“Add to Home screen”</span>.</>
              ) : (
                <>In Chrome or Edge, click the{' '}
                  <span className="text-[#C7A96B]">install icon</span> in the address
                  bar — or open the browser menu and choose{' '}
                  <span className="text-[#C7A96B]">“Install StyleGenome”</span>.</>
              )}
            </p>
          ) : (
            <p className="font-sans text-xs text-white/70 mt-1">Your private stylist, one tap away — works offline-first and full screen.</p>
          )}
          <div className="flex gap-2 mt-3">
            {deferred && (
              <button onClick={install} data-testid="pwa-install-btn"
                className="px-4 py-2 rounded-full bg-[#C7A96B] text-[#0D0D0D] font-sans text-xs font-semibold min-h-[44px]">
                Install app
              </button>
            )}
            <button onClick={dismiss} data-testid="pwa-install-dismiss" aria-label="Dismiss install prompt"
              className="px-4 py-2 rounded-full border border-white/20 font-sans text-xs text-white/80 min-h-[44px]">
              Not now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
